import { defineStore } from 'pinia';
import { IStatusWithButton, useStatusStore } from '@/stores/status';
import { IButton } from '@/components/ComponentTypes';
import { ref } from 'vue';

export type TErrorType = {
    BUSINESS: 'BUSINESS';
    AVAILABILITY: 'AVAILABILITY';
    CREATE: 'CREATE';
    UPDATE: 'UPDATE';
    CANCEL: 'CANCEL';
    REQUEST_ID: 'REQUEST_ID';
};

export interface IError {
    message: string;
    enabled: boolean;
    refresh_required: boolean;
    button?: IButton;
    type?: keyof TErrorType;
}

const stateDefaults: IError = {
    message: '',
    enabled: false,
    refresh_required: false,
};
export const useErrorStore = defineStore('errors', () => {
    const state = ref<IError>({ ...stateDefaults });

    const clearErrors = (): void => {
        useStatusStore().clearStatus();
        Object.assign(state.value, {
            message: '',
            enabled: false,
            refresh_required: false,
        });
    };
    const setError = (error: any): void => {
        const errorData = error.response.data;

        if (errorData?.availabilityError) {
            setErrorWithRefresh();
        } else if (errorData?.issues.length > 0) {
            setErrorIssues(errorData);
        } else {
            const statusStore = useStatusStore();
            Object.assign(state.value, {
                message: errorData.error.message,
                enabled: true,
            });

            statusStore.setStatusError(errorData.error.message);
        }
    };

    const setErrorWithKey = (errorMode: keyof TErrorType) => {
        state.value.type = errorMode;
    };

    const setErrorInternally = (error: any): void => {
        const statusStore = useStatusStore();

        Object.assign(state.value, {
            message: error.message,
            enabled: true,
        });
        statusStore.setStatusError(error.message);
    };
    const setEnquiryError = (): void => {
        Object.assign(state.value, {
            failed_enquiry_request: true,
        });
    };
    const setBookingError = (): void => {
        Object.assign(state.value, {
            failed_booking_request: true,
        });
    };
    const setErrorIssues = (errors: any): void => {
        const statusStore = useStatusStore();
        const errorItems = errors.issues
            .map((issue: any) => `<li>${issue.message}</li>`)
            .join('');
        const compiledError = `<h6 class="font-bold">${errors.error?.message ?? 'There have been one or more issues with your request'}:</h6><ol>${errorItems}</ol>`;

        Object.assign(state.value, {
            message: compiledError,
            enabled: true,
        });
        statusStore.setStatusError(compiledError);
    };
    const setErrorWithRefresh = (): void => {
        const statusStore = useStatusStore();
        const message =
            '<p class="font-bold block mb-2">Slot no longer available</p><p>Sorry, but that date or time is no longer available. Feel free to select another one.</p>';

        Object.assign(state.value, {
            message,
            refresh_required: true,
            enabled: true,
        });

        const statusObject: IStatusWithButton = {
            message,
        };

        statusStore.setStatusWithButton(statusObject);
    };

    return {
        state,
        clearErrors,
        setError,
        setErrorInternally,
        setEnquiryError,
        setBookingError,
        setErrorIssues,
        setErrorWithRefresh,
        setErrorWithKey,
    };
});
